import { systemConfig } from '@/config/system.config';

interface JsonMap {
  [key: string]: unknown;
}

export interface AppConfigPayload {
  config: JsonMap;
  planConfig: JsonMap;
  mergedConfig: JsonMap;
}

const appConfigCache = new Map<string, Promise<AppConfigPayload>>();

export function clearAppConfigCache(): void {
  appConfigCache.clear();
}

async function fetchJsonMap(url: string): Promise<JsonMap> {
  const response = await fetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch config (${response.status})`);
  }
  const json: unknown = await response.json();
  if (typeof json !== 'object' || json === null || Array.isArray(json)) {
    throw new Error('Invalid config response shape');
  }
  return json as JsonMap;
}

export async function fetchAppConfig(tenantId: string): Promise<AppConfigPayload> {
  const cacheKey = tenantId.toLowerCase();
  let cached = appConfigCache.get(cacheKey);
  if (!cached) {
    cached = Promise.all([
      fetchJsonMap(systemConfig.getConfigUrl(tenantId)),
      fetchJsonMap(systemConfig.getPlanningConfigUrl(tenantId)),
    ])
      .then(([config, planConfig]) => ({
        config,
        planConfig,
        mergedConfig: { ...config, ...planConfig },
      }))
      .catch((error: unknown) => {
        appConfigCache.delete(cacheKey);
        throw error;
      });
    appConfigCache.set(cacheKey, cached);
  }
  return cached;
}
